import CryptoJS from 'crypto-js';

/**
 * Calculates the MD5 hash of a file
 * @param file - The File object to hash
 * @returns Promise<string> - The hexadecimal hash string
 */
export async function calculateFileHash(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
    const hash = CryptoJS.MD5(wordArray);
    
    return hash.toString(CryptoJS.enc.Hex);
  } catch (error) {
    console.error('Error calculating file hash:', error);
    throw new Error('Failed to calculate file hash');
  }
}

/**
 * Calculates the MD5 hash of multiple files
 * @param files - Array of File objects to hash
 * @returns Promise<{file: File, hash: string}[]> - Array of files with their hashes
 */
export async function calculateMultipleFileHashes(files: File[]): Promise<{file: File, hash: string}[]> {
  const promises = files.map(async (file) => ({
    file,
    hash: await calculateFileHash(file)
  }));
  
  return Promise.all(promises);
}

/**
 * Generates a unique file identifier combining filename, size, and hash
 * @param file - The File object
 * @returns Promise<string> - Unique identifier string
 */
export async function generateFileIdentifier(file: File): Promise<string> {
  const hash = await calculateFileHash(file);
  return `${file.name}_${file.size}_${hash.substring(0, 8)}`;
}
